import React from 'react';
import styled from 'styled-components';
import { Box, VisuallyHiddenStyles } from '@99designs/emu';
import { borders, colors, spaces, typography } from '@99designs/emu/settings';

const CardWrapper = styled.div<{ checked: boolean; disabled: boolean }>`
    display: flex;
    flex-direction: row;
    padding: ${spaces['1.5bu']} ${spaces['1.5bu']} 0 0;
    border-radius: ${borders.borderRadiuses.medium}px;
    border-width: 1px;
    border-style: solid;
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    border-color: ${({ checked }) => (checked ? colors.grey800 : colors.grey300)};
    position: relative;
`;

const StyledTitle = styled.p`
    font-size: ${typography.typeScale.size3}px;
    font-family: ${typography.fontFamilies.heading};
    margin-bottom: 0;
`;

const StyledSubtitle = styled.p`
    color: ${colors.grey800};
    margin-top: ${spaces['0.25bu']};
    margin-bottom: ${spaces['1.5bu']};
    font-size: ${typography.typeScale.size3}px;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
    // Hide checkbox visually but remain accessible to screen readers.
    ${VisuallyHiddenStyles}
`;

const Icon = styled.svg`
    fill: none;
    stroke: ${colors.forrestLyf};
    stroke-width: ${spaces['0.25bu']};
`;

const StyledCheckbox = styled.div<{ checked: boolean }>`
    display: inline-block;
    width: ${spaces['2bu']};
    height: ${spaces['2bu']};
    margin-right: ${spaces['1bu']};
    margin-left: ${spaces['1bu']};
    flex-shrink: 0;
    border-radius: ${borders.borderRadiuses.small}px;
    border: 1px solid ${colors.grey500};
    cursor: pointer;

    ${HiddenCheckbox}:focus + & {
        box-shadow: 0 0 0 3px ${colors.grey500};
    }

    ${Icon} {
        visibility: ${({ checked }) => (checked ? 'visible' : 'hidden')};
    }
`;

function Tick({ isVisible }: { isVisible: boolean }) {
    return isVisible ? (
        <Icon viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" />
        </Icon>
    ) : null;
}

interface Props {
    id: string;
    description: string;
    subText: React.ReactNode;
    label?: React.ReactNode;
    children?: React.ReactNode;
    isChecked?: boolean;
    isDisabled?: boolean;
    onChecked?: (value: string) => void;
}

function CheckboxCard({
    id,
    description,
    subText,
    label,
    children,
    isChecked = false,
    isDisabled = false,
    onChecked,
    ...props
}: Props) {
    function toggleChecked() {
        if (onChecked && !isDisabled) {
            onChecked(id);
        }
    }

    return (
        <CardWrapper checked={isChecked} disabled={isDisabled}>
            <Box>
                <HiddenCheckbox checked={isChecked} onChange={toggleChecked} {...props} />
                <StyledCheckbox
                    checked={isChecked}
                    onClick={toggleChecked}
                    data-testid={`checkbox-${description}`}
                >
                    <Tick isVisible={isChecked}></Tick>
                </StyledCheckbox>
            </Box>
            <Box width="100%">
                <Box display="flex" flexDirection="row" justifyContent="space-between">
                    <StyledTitle>{description}</StyledTitle>
                    <StyledTitle>{label}</StyledTitle>
                </Box>
                <StyledSubtitle>{subText}</StyledSubtitle>
                {children && <Box marginBottom="1.5bu">{children}</Box>}
            </Box>
        </CardWrapper>
    );
}

export default CheckboxCard;
